/**
 * OpenSolar SDK Hook
 *
 * This hook provides a React interface to the OpenSolar SDK.
 * It handles SDK initialization, events, and methods.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSdkTokens } from '@/lib/opensolar-token-manager';
import { QueueProcessedEvent } from '@shared/opensolar-types';
import { toast } from '@/hooks/use-toast';

// Options for the OpenSolar SDK hook
export interface UseOpenSolarSdkOptions {
  onDesignDataFetched?: (data: any) => void;
  onQueueProcessed?: (event: QueueProcessedEvent) => void;
  onIdle?: (idle: boolean) => void;
  onDirtyFieldsChanged?: (hasDirty: boolean, fields?: string[]) => void;
}

export function useOpenSolarSdk(options: UseOpenSolarSdkOptions = {}) {
  // State tracking
  const [loaded, setLoaded] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasDirtyFields, setHasDirtyFields] = useState(false);
  const [designData, setDesignData] = useState<any>(null);

  // SDK instance reference
  const sdkRef = useRef<any>(null);

  // Options reference to avoid dependency array issues
  const optionsRef = useRef(options);

  // Update options when they change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Function to be passed to the OsSdkView component's onLoad prop
  const handleSdkLoad = useCallback((sdk: any) => {
    if (!sdk) return;

    console.log('OpenSolar SDK loaded', sdk);
    sdkRef.current = sdk;
    setLoaded(true);

    // Set up event listeners
    if (sdk.addEventListener) {
      sdk.addEventListener('queueProcessed', (event: any) => {
        const eventIdle = event?.detail?.idle || false;
        const eventDirty = event?.detail?.hasDirtyFields || false;
        const dirtyFields = event?.detail?.dirtyFields || [];

        setIsIdle(eventIdle);
        setHasDirtyFields(eventDirty);

        // Call user callbacks
        if (optionsRef.current.onQueueProcessed) {
          optionsRef.current.onQueueProcessed({
            idle: eventIdle,
            hasDirtyFields: eventDirty,
            dirtyFields,
          });
        }

        if (optionsRef.current.onIdle) {
          optionsRef.current.onIdle(eventIdle);
        }

        if (optionsRef.current.onDirtyFieldsChanged) {
          optionsRef.current.onDirtyFieldsChanged(eventDirty, dirtyFields);
        }
      });
    }
  }, []);

  // Save the project
  const saveProject = useCallback(async () => {
    if (!sdkRef.current || !isIdle) {
      console.warn('Cannot save: SDK not idle or not initialized');
      return false;
    }

    try {
      setIsSaving(true);

      await sdkRef.current.project_form.save();

      setIsSaving(false);
      setHasDirtyFields(false);
      return true;
    } catch (error) {
      console.error('Error saving project:', error);
      setIsSaving(false);

      toast({
        title: 'Save Error',
        description: 'Failed to save the project',
        variant: 'destructive',
      });
      return false;
    }
  }, [isIdle]);

  // Fetch design data
  const fetchDesignData = useCallback(async () => {
    if (!sdkRef.current || !isIdle) {
      console.warn('Cannot fetch design data: SDK not idle or not initialized');
      return null;
    }

    try {
      const data = await sdkRef.current.project_form.getDesignData();
      setDesignData(data);

      if (optionsRef.current.onDesignDataFetched) {
        optionsRef.current.onDesignDataFetched(data);
      }

      return data;
    } catch (error) {
      console.error('Error fetching design data:', error);

      toast({
        title: 'Design Data Error',
        description: 'Failed to fetch design data',
        variant: 'destructive',
      });
      return null;
    }
  }, [isIdle]);

  return {
    sdkRef,
    loaded,
    isIdle,
    isSaving,
    hasDirtyFields,
    designData,
    handleSdkLoad,
    saveProject,
    fetchDesignData,
  };
}
